import React, { useState, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import Modal from "aldoo-ra/Modal"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import NewsletterAPI from "../api"

// Import step components
import BasicInfoStep from "./steps/BasicInfoStep"
import TemplatesStep from "./steps/TemplatesStep"
import AudienceStep from "./steps/AudienceStep"
import ReviewStep from "./steps/ReviewStep"

// Import shared components
import StepHeader from "./components/StepHeader"
import StepNavigation from "./components/StepNavigation"
import AudiencePreview from "./steps/AudienceStep/components/AudiencePreview"

// Import constants
import { INITIAL_FORM_DATA, STEPS } from "./constants"

const ReleaseGenerator = ({ isOpen, onClose, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState(INITIAL_FORM_DATA)
  const [errors, setErrors] = useState({})
  const [audiencePreview, setAudiencePreview] = useState(null)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [completedSteps, setCompletedSteps] = useState(new Set())

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1)
      setFormData(INITIAL_FORM_DATA)
      setErrors({})
      setAudiencePreview(null)
      setCompletedSteps(new Set())
    }
  }, [isOpen])

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))

    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  // Clear specific field errors
  const clearFieldError = (fieldName) => {
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[fieldName]
        return newErrors
      })
    }
  }

  // Validate current step
  const validateStep = (step) => {
    const newErrors = {}

    switch (step) {
      case 1: // Basic Info
        if (!formData.name.trim()) {
          newErrors.name = "Release name is required"
        }
        if (!formData.scheduledAt) {
          newErrors.scheduledAt = "Scheduled date and time is required"
        } else {
          const scheduledDate = new Date(formData.scheduledAt)
          const now = new Date()
          if (scheduledDate <= now) {
            newErrors.scheduledAt = "Scheduled time must be in the future"
          }
        }
        break

      case 2: // Templates
        if (formData.templates.length === 0) {
          newErrors.templates = "At least one template is required"
        } else {
          // Validate each template
          formData.templates.forEach((template, index) => {
            if (!template.templateId) {
              newErrors[`template_${index}_id`] = "Template selection is required"
            }
            if (template.percentage <= 0 || template.percentage > 100) {
              newErrors[`template_${index}_percentage`] = "Percentage must be between 1 and 100"
            }
          })

          // Validate total percentage
          const totalPercentage = formData.templates.reduce((sum, template) => sum + (template.percentage || 0), 0)

          if (totalPercentage > 100) {
            newErrors.templates = `Total percentage is ${totalPercentage}% - cannot exceed 100%`
          } else if (totalPercentage < 100) {
            newErrors.templates = `Total percentage is ${totalPercentage}% - must equal 100%`
          }
        }
        break

      case 3: // Audience
        // Now requires specific selections - no more "empty = all" behavior

        if (formData.segments.length === 0) {
          newErrors.segments = "Please select at least one audience segment"
        }

        if (formData.targetLanguages.length === 0) {
          newErrors.targetLanguages = "Please select at least one target language"
        }

        if (formData.targetPlatforms.length === 0) {
          newErrors.targetPlatforms = "Please select at least one target platform"
        }

        if (!formData.audienceShare || formData.audienceShare <= 0 || formData.audienceShare > 100) {
          newErrors.audienceShare = "Audience share must be greater than 0% and at most 100%"
        }
        break

      case 4: // Review
        // Final validation - check all previous steps
        const step1Valid = validateStep(1)
        const step2Valid = validateStep(2)
        const step3Valid = validateStep(3)

        if (!step1Valid || !step2Valid || !step3Valid) {
          newErrors.general = "Please fix errors in previous steps"
        }
        break
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle step navigation
  const handleNext = () => {
    if (validateStep(currentStep)) {
      // Mark current step as completed
      setCompletedSteps(prev => new Set([...prev, currentStep]))
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length))
    } else {
      // Show notification when validation fails
      showNotification({
        message: "Please fix the highlighted errors before proceeding",
        className: "bg-admin_error text-white"
      })

      // Scroll to the first error element when validation fails
      setTimeout(() => {
        const firstError = document.querySelector('.text-admin_error')
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
    }
  }

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1))
  }

  const handleStepClick = (step) => {
    // Allow navigation to any step
    setCurrentStep(step)
  }

  // Update audience preview when on audience step or review step
  const updateAudiencePreview = async () => {
    if ((currentStep === 3 || currentStep === 4) && formData.segments.length > 0) {
      try {
        setLoadingPreview(true)
        // Filter out "any" from languages and platforms - empty array means no filter
        const filteredLanguages = formData.targetLanguages.includes('any') ? [] : formData.targetLanguages
        const filteredPlatforms = formData.targetPlatforms.includes('any') ? [] : formData.targetPlatforms

        const response = await NewsletterAPI.previewAudience(
          formData.segments,
          formData.includeUnregistered,
          filteredLanguages,
          filteredPlatforms,
          formData.ltvAmount,
          formData.ltvOperator,
          formData.deliveryFilterEnabled ? formData.deliveryOperator : null,
          formData.deliveryFilterEnabled ? formData.deliveryReleases : [],
          formData.audienceShare
        )

        if (response.success || response.audienceSize !== undefined) {
          setAudiencePreview(response.preview || response)
        }
      } catch (error) {
        console.error('Failed to preview audience:', error)
      } finally {
        setLoadingPreview(false)
      }
    }
  }

  // Update audience when form data changes
  useEffect(() => {
    updateAudiencePreview()
  }, [currentStep, formData.segments, formData.includeUnregistered, formData.targetLanguages, formData.targetPlatforms, formData.ltvAmount, formData.ltvOperator, formData.deliveryFilterEnabled, formData.deliveryOperator, formData.deliveryReleases, formData.audienceShare])

  // Create the release
  const handleCreateRelease = async () => {
    if (!validateStep(4)) return

    try {
      setIsCreating(true)

      // Filter out "any" from languages and platforms - empty array means no filter
      // Only apply filters if they are enabled
      const filteredLanguages = formData.targetLanguagesEnabled
        ? (formData.targetLanguages.includes('any') ? [] : formData.targetLanguages)
        : []
      const filteredPlatforms = formData.targetPlatformsEnabled
        ? (formData.targetPlatforms.includes('any') ? [] : formData.targetPlatforms)
        : []

      // Convert local datetime to ISO string with timezone offset
      // datetime-local input gives "YYYY-MM-DDTHH:mm" in local time
      // We need to convert it to proper ISO format with timezone
      const localDate = new Date(formData.scheduledAt)
      const scheduledAtISO = localDate.toISOString()

      const releaseData = {
        name: formData.name,
        description: formData.description,
        scheduledAt: scheduledAtISO,
        templates: formData.templates,
        segments: formData.segments,
        includeUnregistered: formData.includeUnregistered,
        targetLanguages: filteredLanguages,
        targetPlatforms: filteredPlatforms,
        ltvFilterEnabled: formData.ltvFilterEnabled,
        ltvAmount: formData.ltvFilterEnabled ? formData.ltvAmount : null,
        ltvOperator: formData.ltvFilterEnabled ? formData.ltvOperator : 'gte',
        deliveryFilterEnabled: formData.deliveryFilterEnabled,
        deliveryOperator: formData.deliveryFilterEnabled ? formData.deliveryOperator : null,
        deliveryReleases: formData.deliveryFilterEnabled ? formData.deliveryReleases : [],
        audienceShare: formData.audienceShareEnabled ? formData.audienceShare : 100
        // Sending rate is determined dynamically by email provider quotas
        // maxRetries will use backend default (3 retries)
      }

      const response = await NewsletterAPI.createRelease(releaseData)

      if (response.success) {
        showNotification({
          message: `Newsletter release "${formData.name}" created successfully!`,
          className: "bg-admin_success text-white"
        })

        // Call success callback immediately to refresh the table
        onSuccess && onSuccess(response.release)

        // Show success dialog
        showPromptDialog({
          variant: "admin",
          title: "🎉 Release Created!",
          message: `Your newsletter release "${formData.name}" has been created and scheduled.\n\nScheduled for: ${new Date(formData.scheduledAt).toLocaleString()}\nRecipients: ${response.release?.audienceSize?.toLocaleString() || 'Unknown'}`,
          yesLabel: "View Releases",
          noLabel: "Create Another",
          onConfirm: () => {
            onClose()
          },
          onCancel: () => {
            // Reset form for creating another release
            setCurrentStep(1)
            setFormData(INITIAL_FORM_DATA)
            setErrors({})
            setAudiencePreview(null)
          }
        })

      } else {
        throw new Error(response.message || "Failed to create release")
      }

    } catch (error) {
      console.error('Failed to create release:', error)
      showNotification({
        message: "Failed to create release: " + error.message,
        className: "bg-admin_error text-white"
      })
    } finally {
      setIsCreating(false)
    }
  }

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        // For step 1, allow proceeding but let validation handle the errors
        return true
      case 2:
        // For step 2, allow proceeding but let validation handle the errors
        return true
      case 3:
        return true // Allow proceeding with empty selections (means "include all")
      case 4:
        return true
      default:
        return false
    }
  }

  // Common props for all step components
  const commonStepProps = {
    formData,
    errors,
    onInputChange: handleInputChange,
    clearFieldError
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Newsletter Release"
      variant="admin_centered"
      className="max-w-4xl"
      closeOnEscape={false}
      closeOnClickOutside={false}
      styleOverrides={{
        content: "px-5 sm:px-6" // Remove bottom padding for sticky audience preview
      }}
      footerContent={
        <StepNavigation
          currentStep={currentStep}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onGenerate={handleCreateRelease}
          canProceed={canProceed() && !isCreating}
        />
      }
    >
      <div className="flex flex-col h-[600px]">
        {/* Sticky Header */}
        <div className="flex-shrink-0 border-b border-admin_bordered">
          <StepHeader
            steps={STEPS}
            currentStep={currentStep}
            completedSteps={completedSteps}
            onStepClick={handleStepClick}
            formData={formData}
            audiencePreview={audiencePreview}
          />
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper px-4 pt-4">
          {isCreating && (
            <div className="bg-admin_primary/10 border border-admin_primary rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <div className="h-5 w-5 rounded-full border-b-2 border-admin_primary mr-3 animate-spin" />

                <Typography
                  variant="admin_body1"
                  className="text-admin_primary"
                >
                  Creating newsletter release...
                </Typography>
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <BasicInfoStep {...commonStepProps} />
          )}

          {currentStep === 2 && (
            <TemplatesStep {...commonStepProps} />
          )}

          {currentStep === 3 && (
            <AudienceStep {...commonStepProps} />
          )}

          {currentStep === 4 && (
            <ReviewStep
              formData={formData}
              audiencePreview={audiencePreview}
              errors={errors}
            />
          )}

          {/* General errors */}
          {errors.general && (
            <div className="bg-admin_error/10 border border-admin_error rounded-lg p-4 mt-4">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="w-5 h-5 text-admin_error mr-2" />

                <Typography
                  variant="admin_body1"
                  className="text-admin_error my-0"
                >
                  {errors.general}
                </Typography>
              </div>
            </div>
          )}
        </div>

        {/* Audience Preview - Sticky at bottom when on step 3 */}
        {currentStep === 3 && formData.segments.length > 0 && (
          <div className="flex-shrink-0">
            <AudiencePreview
              audiencePreview={audiencePreview}
              loadingPreview={loadingPreview}
            />
          </div>
        )}
      </div>
    </Modal>
  )
}

export default ReleaseGenerator